import { useMemo, useState } from 'react';
import { cn } from '../../lib/cn';
import { useCollaborators } from '../../hooks/queries/useCollaborators';
import { useAuthStore } from '../../store/useAuthStore';
import { Avatar } from './Avatar';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

/** Valeur de l'option « personne » (non assigné), ajoutée par `unassignedLabel`. */
export const PERSON_NONE = 'none';

interface Person {
  id: string;
  name: string;
  role: string;
  avatarUrl: string | null;
}

const ROLE_GROUPS: readonly { role: string; label: string }[] = [
  { role: 'admin', label: 'Administrateur' },
  { role: 'lawyer', label: 'Avocats' },
  { role: 'secretary', label: 'Assistant(e)s' },
];

export interface PersonSelectProps {
  /** Identifiants de collaborateurs sélectionnés (ou PERSON_NONE). */
  value: string[];
  onChange: (value: string[]) => void;
  /** Choix unique : boutons radio, la liste se ferme au clic. */
  single?: boolean;
  /** Choix unique obligatoire, ou au moins une personne en choix multiple. */
  required?: boolean;
  /** Pleine largeur, pour les formulaires et les modales. */
  fluid?: boolean;
  /** Nom pluriel du groupe : « 3 avocats ». */
  label?: string;
  /** Texte sans sélection : « Tous les avocats ». */
  emptyLabel?: string;
  /** Rappel du filtre devant le texte : « Responsable ». Rend le bouton en pointillés quand il est vide. */
  prefix?: string;
  roles?: readonly string[];
  excludeIds?: readonly string[];
  /** Ajoute en tête l'option « personne » avec ce libellé. */
  unassignedLabel?: string;
  /** Pastille à droite du nom, par identifiant. */
  hints?: Record<string, string>;
  disabled?: boolean;
  className?: string;
}

/**
 * Sélecteur de personnes unique de l'application : filtres, création,
 * assignation, responsable. En choix multiple, la sélection ne s'applique
 * qu'à « Valider » (clic dehors ou Échap = annuler).
 */
export function PersonSelect({
  value,
  onChange,
  single = false,
  required = false,
  fluid = false,
  label = 'intervenants',
  emptyLabel = 'Choisir…',
  prefix,
  roles,
  excludeIds,
  unassignedLabel,
  hints,
  disabled,
  className,
}: PersonSelectProps) {
  const { data: collaborators } = useCollaborators();
  const meId = useAuthStore((s) => s.collaborator?.id);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<string[]>([]);
  const [query, setQuery] = useState('');

  const people = useMemo<Person[]>(() => {
    const list = (collaborators ?? [])
      // Une personne désactivée reste proposée si elle est déjà sélectionnée.
      .filter((c) => c.is_active || value.includes(c.id))
      .filter((c) => !roles || roles.includes(c.role))
      .filter((c) => !excludeIds?.includes(c.id))
      .map((c) => ({
        id: c.id,
        name: c.full_name?.trim() || c.email,
        role: c.role,
        avatarUrl: c.avatar_url,
      }))
      .sort((a, b) => a.name.localeCompare(b.name, 'fr', { sensitivity: 'base' }));
    return unassignedLabel
      ? [{ id: PERSON_NONE, name: unassignedLabel, role: PERSON_NONE, avatarUrl: null }, ...list]
      : list;
  }, [collaborators, roles, excludeIds, unassignedLabel, value]);

  const selected = people.filter((p) => value.includes(p.id));
  const count = selected.length;
  const on = count > 0 && !fluid;

  function toggleOpen(next: boolean) {
    if (next) {
      setDraft(value);
      setQuery('');
    }
    setOpen(next);
  }

  function commit(next: string[]) {
    setOpen(false);
    const ordered = people.map((p) => p.id).filter((id) => next.includes(id));
    if (ordered.join() !== selected.map((p) => p.id).join()) onChange(ordered);
  }

  function pick(id: string, checked: boolean) {
    if (single) {
      commit(checked || required ? [id] : []);
      return;
    }
    setDraft((current) => (checked ? [...current, id] : current.filter((x) => x !== id)));
  }

  const needle = query.trim().toLowerCase();
  const hits = people.filter((p) => !needle || p.name.toLowerCase().includes(needle));
  const groups = [
    { role: PERSON_NONE, label: '' },
    ...ROLE_GROUPS,
    // Rôle inconnu de la liste : regroupé à la fin, sans titre.
    { role: '*', label: '' },
  ].map((group) => ({
    ...group,
    people: hits.filter((p) =>
      group.role === '*'
        ? p.role !== PERSON_NONE && !ROLE_GROUPS.some((g) => g.role === p.role)
        : p.role === group.role,
    ),
  }));

  let text = emptyLabel;
  const first = selected[0];
  if (count === 1 && first) text = first.name + (first.id === meId ? ' (vous)' : '');
  else if (count > 1) text = `${count} ${label}`;

  const shownAvatars = selected.filter((p) => p.id !== PERSON_NONE).slice(0, 3);

  return (
    <Popover open={open} onOpenChange={toggleOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-haspopup="listbox"
          title={count ? selected.map((p) => p.name).join(', ') : text}
          className={cn(
            'inline-flex min-w-0 max-w-full items-center gap-[7px] border bg-white pl-1.5 pr-2.5 text-[13px] text-cime-text-primary transition-colors',
            'hover:border-cime-primary data-[state=open]:border-cime-primary data-[state=open]:shadow-[0_0_0_3px_rgba(4,52,41,0.1)]',
            'disabled:cursor-not-allowed disabled:opacity-60',
            fluid ? 'h-9 w-full rounded-lg border-neutral-300' : 'h-8 rounded-full border-neutral-300',
            on && 'border-cime-primary bg-[#e8f0ec] font-semibold text-cime-primary',
            prefix && !on && !fluid && 'border-dashed text-cime-text-muted',
            className,
          )}
        >
          {shownAvatars.length > 0 ? (
            <span className="flex flex-shrink-0 -space-x-1.5">
              {shownAvatars.map((p) => (
                <Avatar
                  key={p.id}
                  name={p.name}
                  src={p.avatarUrl}
                  size="xs"
                  className="h-[22px] w-[22px] text-[10px] ring-1 ring-white"
                />
              ))}
            </span>
          ) : (
            <Placeholder className="h-[22px] w-[22px]" />
          )}
          <span className="min-w-0 flex-1 truncate text-left">
            {prefix ? <span className="mr-1">{prefix}</span> : null}
            {text}
          </span>
          {count > 3 ? (
            <b className="grid h-[18px] min-w-5 flex-shrink-0 place-items-center rounded-full bg-cime-primary px-[5px] text-[11px] font-bold text-white">
              +{count - 3}
            </b>
          ) : null}
          <svg
            viewBox="0 0 512 512"
            fill="currentColor"
            aria-hidden
            className={cn('h-2.5 w-2.5 flex-shrink-0 text-neutral-400 transition-transform', open && 'rotate-180')}
          >
            {/* Font Awesome Pro 6.5.1 — chevron-down regular */}
            <path d="M239 401c9.4 9.4 24.6 9.4 33.9 0L465 209c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-175 175L81 175c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9L239 401z" />
          </svg>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        role="listbox"
        aria-multiselectable={!single}
        className={cn(
          'overflow-hidden rounded-xl border-neutral-200 bg-white p-0 shadow-[0_16px_40px_rgba(4,52,41,0.12)]',
          fluid ? 'w-[max(268px,var(--radix-popover-trigger-width))]' : 'w-[268px]',
        )}
        onKeyDown={(e) => {
          if (e.key !== 'Enter') return;
          e.preventDefault();
          const top = hits[0];
          if (!single) commit(draft);
          else if (top) commit([top.id]);
        }}
      >
        <label className="flex items-center gap-2 border-b border-neutral-100 px-3 py-[9px] text-neutral-400">
          <svg viewBox="0 0 512 512" className="h-3 w-3 flex-shrink-0" fill="currentColor" aria-hidden>
            {/* Font Awesome Pro 6.5.1 — magnifying-glass regular */}
            <path d="M368 208A160 160 0 1 0 48 208a160 160 0 1 0 320 0zM337.1 371.1C301.7 399.2 256.8 416 208 416C93.1 416 0 322.9 0 208S93.1 0 208 0S416 93.1 416 208c0 48.8-16.8 93.7-44.9 129.1L505 471c9.4 9.4 9.4 24.6 0 33.9s-24.6 9.4-33.9 0L337.1 371.1z" />
          </svg>
          <input
            type="search"
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher…"
            aria-label="Rechercher une personne"
            autoComplete="off"
            className="min-w-0 flex-1 border-0 bg-transparent text-[13px] text-cime-text-primary outline-none"
          />
        </label>
        <div className="max-h-[260px] overflow-y-auto p-1">
          {hits.length === 0 ? (
            <p className="p-3.5 text-center text-[13px] text-neutral-400">Aucun collaborateur trouvé.</p>
          ) : (
            groups.map((group) =>
              group.people.length === 0 ? null : (
                <div key={group.role}>
                  {group.label ? (
                    <p className="m-0 px-2 pb-[3px] pt-2 text-[10px] font-bold uppercase tracking-[0.08em] text-neutral-400">
                      {group.label}
                    </p>
                  ) : null}
                  {group.people.map((p) => {
                    const checked = (single ? value : draft).includes(p.id);
                    return (
                      <label
                        key={p.id}
                        className="flex cursor-pointer items-center gap-[9px] rounded-[7px] px-2 py-1.5 text-[13px] hover:bg-[#fbfaf6]"
                      >
                        <input
                          type={single ? 'radio' : 'checkbox'}
                          name="person-select"
                          checked={checked}
                          onChange={(e) => pick(p.id, e.target.checked)}
                          onClick={() => {
                            // Radio déjà coché : un clic le retire si le choix n'est pas obligatoire.
                            if (single && checked) pick(p.id, false);
                          }}
                          className="m-0 h-3.5 w-3.5 flex-shrink-0 accent-cime-primary"
                        />
                        {p.id === PERSON_NONE ? (
                          <Placeholder className="h-6 w-6" />
                        ) : (
                          <Avatar name={p.name} src={p.avatarUrl} size="xs" className="h-6 w-6 text-[10px]" />
                        )}
                        <span className={cn('min-w-0 flex-1 truncate', checked && 'font-semibold text-cime-primary')}>
                          {p.name}
                          {p.id === meId ? (
                            <small className="ml-1 text-xs font-normal text-cime-text-muted">(vous)</small>
                          ) : null}
                        </span>
                        {hints?.[p.id] ? (
                          <span className="flex-shrink-0 rounded-full bg-[#e8f0ec] px-1.5 py-px text-[11px] text-cime-primary">
                            {hints[p.id]}
                          </span>
                        ) : null}
                      </label>
                    );
                  })}
                </div>
              ),
            )
          )}
        </div>
        {single ? null : (
          <div className="flex items-center gap-1 border-t border-neutral-100 bg-[#fbfaf6] px-2.5 py-2">
            <FooterLink onClick={() => setDraft(people.map((p) => p.id))}>Tout</FooterLink>
            <FooterLink onClick={() => setDraft([])}>Aucun</FooterLink>
            <span className="flex-1" />
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="h-7 rounded-full px-2.5 text-xs text-cime-text-muted hover:bg-neutral-100"
            >
              Annuler
            </button>
            <button
              type="button"
              disabled={required && draft.length === 0}
              onClick={() => commit(draft)}
              className="h-7 rounded-full bg-cime-primary px-3 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-40"
            >
              Valider
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

function FooterLink({ onClick, children }: { onClick: () => void; children: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-md px-1.5 py-1 text-xs text-cime-primary hover:bg-[#e8f0ec]"
    >
      {children}
    </button>
  );
}

function Placeholder({ className }: { className?: string }) {
  return (
    <span className={cn('grid flex-shrink-0 place-items-center rounded-full bg-neutral-100 text-neutral-400', className)}>
      <svg viewBox="0 0 640 512" className="h-3 w-3" fill="currentColor" aria-hidden>
        {/* Font Awesome Pro 6.5.1 — user-group regular */}
        <path d="M224 48a80 80 0 1 1 0 160 80 80 0 1 1 0-160zm0 208A128 128 0 1 0 224 0a128 128 0 1 0 0 256zm-45.7 96h91.4c65.7 0 120.1 48.7 129 112H49.3c8.9-63.3 63.3-112 129-112zm0-48C79.8 304 0 383.8 0 482.3C0 498.7 13.3 512 29.7 512H418.3c16.4 0 29.7-13.3 29.7-29.7C448 383.8 368.2 304 269.7 304H178.3zm431 208c17 0 30.7-13.8 30.7-30.7C640 392.2 567.8 320 478.7 320H417.3c-4.4 0-8.8 .2-13.2 .5c46.4 38.6 75.9 96.7 75.9 161.8c0 10.8-2.8 20.9-7.6 29.7H609.3zM432 256c61.9 0 112-50.1 112-112s-50.1-112-112-112c-24.8 0-47.7 8.1-66.3 21.7C377.4 75.9 384 101.2 384 128c0 35.6-11.6 68.5-31.3 95.1C373 243.4 401 256 432 256z" />
      </svg>
    </span>
  );
}
